/**
 * 配信詳細の「戻る」の行き先。
 *
 * 配信詳細（`/deliveries/:id`）へは複数の画面から来る（一斉配信の一覧・
 * シリーズ詳細の配信履歴・シリーズ詳細の比較グラフ）。ブラウザの戻るは
 * それぞれの遷移元へ戻るのに、画面の「戻る」ボタンが一覧に決め打ちだと、
 * シリーズから来た人だけ別の画面へ飛ばされる。
 *
 * そこで遷移元のリンクが `location.state` に自分のパスを載せ、配信詳細は
 * それを読んで戻る。`navigate(-1)` にしないのは、履歴の 1 つ前がアプリの
 * 画面とは限らないため（新しいタブで開いた・URL を直接開いた・ブックマーク）。
 * その場合は `state` が無いので、既定の一覧へ落とす。
 */
import type { Location } from "react-router-dom";

/** 遷移元が分からないときの行き先（一斉配信の一覧） */
export const DELIVERY_BACK_FALLBACK = "/deliveries";

/** 配信詳細へ遷移するリンクが `state` に載せる値 */
export type DeliveryBackState = { from: string };

/**
 * 配信詳細へのリンクに載せる `state` を作る。
 *
 * クエリも含めるのは、一覧のタブ（`?tab=`）や表示形式（`?view=grouped`）を
 * 戻ったときに保つため。
 * @param {object} location 遷移元の location（`useLocation()` の値）
 * @param {string} location.pathname パス
 * @param {string} location.search クエリ文字列（`?` から。無ければ空文字）
 * @return {DeliveryBackState} リンクの `state` に渡す値
 */
export const deliveryBackState = (
  location: Pick<Location, "pathname" | "search">,
): DeliveryBackState => ({ from: `${location.pathname}${location.search}` });

/**
 * `location.state` から「戻る」の行き先を決める。
 *
 * `state` は履歴に残るただの値で型は保証されない（古い履歴・別の画面が
 * 入れた値がありうる）。そのまま `navigate` に渡すとアプリの外へ飛ばせて
 * しまうので、**`/` で始まるアプリ内のパスだけ**を通す。`//host` や
 * `/\host` はブラウザが別サイトの URL として解釈するので弾く。
 * @param {unknown} state `useLocation().state`
 * @return {string} 戻り先のパス
 */
export const resolveDeliveryBackTo = (state: unknown): string => {
  if (typeof state !== "object" || state === null || !("from" in state)) {
    return DELIVERY_BACK_FALLBACK;
  }
  const { from } = state;
  if (typeof from !== "string" || !from.startsWith("/")) {
    return DELIVERY_BACK_FALLBACK;
  }
  const second = from.charAt(1);
  return second === "/" || second === "\\" ? DELIVERY_BACK_FALLBACK : from;
};
